import React, { useEffect, useState } from 'react';
import { fetchUsers, createUser, updateUser, deleteUser } from '../api';

const UsersTable = () => {
  const [data, setData] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // ------------------------------------------------------------------
  // 🌐 SMART API URL CONFIGURATION
  // ------------------------------------------------------------------
  const isLocal = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
  
  const API_BASE_URL = isLocal 
    ? "http://localhost:5279"                 // Local Development URL
    : "https://beamserver.nsfdcdigital.in";   // Live Production URL

  // ------------------------------------------------------------------

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobileNumber: '',
    address: '',
    state: '',
    city: '',
    password: ''
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = () => {
    setIsLoading(true);
    fetchUsers()
      .then(res => {
        setData(res.data);
        setIsLoading(false);
      })
      .catch(err => {
        console.error("Failed to load users:", err);
        setIsLoading(false);
      });
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await updateUser(editingId, formData);
      } else {
        await createUser(formData);
      }
      handleCancel();
      loadUsers();
    } catch (error) {
      alert("Operation failed: " + error.message);
    }
  };

  const handleEdit = (user) => {
    setEditingId(user.id);
    setFormData({
      name: user.name,
      email: user.email,
      mobileNumber: user.mobileNumber,
      address: user.address,
      state: user.state || '',
      city: user.city,
      password: user.password 
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        await deleteUser(id);
        loadUsers();
      } catch (error) {
        alert("Failed to delete");
      }
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({ name: '', email: '', mobileNumber: '', address: '', state: '', city: '', password: '' });
  };

  const handleImageError = (e) => {
    e.target.src = "https://via.placeholder.com/40?text=User"; 
  };

  return (
    <div className="container-fluid mt-4 px-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="text-black fw-bold">Registered Users</h4>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>
          <i className="bi bi-person-plus-fill me-2"></i>Add New User
        </button>
      </div>

      {/* --- ADD/EDIT FORM --- */}
      {showForm && (
        <div className="card mb-4 p-4 shadow-sm border-primary">
          <h4 className="mb-3 text-primary">{editingId ? 'Edit User Details' : 'Register New User'}</h4>
          <form onSubmit={handleSubmit}>
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label">Full Name</label>
                <input type="text" name="name" className="form-control" value={formData.name} onChange={handleInputChange} required />
              </div>
              <div className="col-md-4">
                <label className="form-label">Email Address</label>
                <input type="email" name="email" className="form-control" value={formData.email} onChange={handleInputChange} required />
              </div>
              <div className="col-md-4">
                <label className="form-label">Mobile Number</label>
                <input type="text" name="mobileNumber" className="form-control" value={formData.mobileNumber} onChange={handleInputChange} required />
              </div>
              
              <div className="col-md-6">
                <label className="form-label">Address</label>
                <input type="text" name="address" className="form-control" value={formData.address} onChange={handleInputChange} />
              </div>
              <div className="col-md-3">
                <label className="form-label">State</label>
                <input type="text" name="state" className="form-control" value={formData.state} onChange={handleInputChange} />
              </div>
              <div className="col-md-3">
                <label className="form-label">City</label>
                <input type="text" name="city" className="form-control" value={formData.city} onChange={handleInputChange} />
              </div>
              
              <div className="col-md-4">
                <label className="form-label">Password</label>
                <input type="text" name="password" className="form-control" value={formData.password} onChange={handleInputChange} placeholder="Enter password" />
              </div>
            </div>
            <div className="mt-4 text-end">
              <button type="button" className="btn btn-secondary me-2" onClick={handleCancel}>Cancel</button>
              <button type="submit" className="btn btn-success px-4">{editingId ? 'Update Profile' : 'Create User'}</button>
            </div>
          </form>
        </div>
      )}

      {/* --- USER TABLE --- */}
      {isLoading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status"></div>
          <p className="mt-2 text-muted">Loading Users...</p>
        </div>
      ) : (
        <div className="table-responsive shadow-sm rounded">
          <table className="table table-hover table-striped table-bordered align-middle mb-0">
            <thead className="table-dark text-center align-middle">
              <tr>
                <th>ID</th>
                <th>Full Name & Profile</th>
                <th>Mobile Number</th>
                <th>Email Address</th>
                <th>Address</th>
                <th>State</th>
                <th>City</th>
                <th>Password</th>
                <th style={{ minWidth: '160px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.map((item, index) => (
                <tr key={item.id}>
                  <td className="text-center fw-bold">{index + 1}</td>
                  
                  <td>
                    <div className="d-flex align-items-center">
                      <img 
                        src={
                          item.profilePicturePath 
                            ? `${API_BASE_URL}/${item.profilePicturePath}` 
                            : "https://via.placeholder.com/40?text=User"
                        } 
                        alt="Profile" 
                        className="rounded-circle me-3"
                        style={{ width: '45px', height: '45px', objectFit: 'cover', border: '2px solid #e0e0e0' }}
                        onError={handleImageError}
                      />
                      <span className="fw-bold text-dark">{item.name}</span>
                    </div>
                  </td>

                  <td className="text-center">{item.mobileNumber}</td>
                  <td>{item.email}</td>
                  <td>
                    <span className="d-inline-block text-truncate" style={{maxWidth: '150px'}} title={item.address}>
                      {item.address || '-'}
                    </span>
                  </td>
                  <td>{item.state || '-'}</td>
                  <td>{item.city || '-'}</td>
                  <td className="text-muted small fst-italic">{item.password}</td>
                  
                  {/* --- UPDATED ACTIONS COLUMN WITH LABELS --- */}
                  <td className="text-center">
                    <button 
                      className="btn btn-sm btn-outline-primary me-2" 
                      onClick={() => handleEdit(item)}
                    >
                      <i className="bi bi-pencil-square me-1"></i> Edit
                    </button>
                    <button 
                      className="btn btn-sm btn-outline-danger" 
                      onClick={() => handleDelete(item.id)}
                    >
                      <i className="bi bi-trash me-1"></i> Delete
                    </button>
                  </td>
                  {/* ------------------------------------------ */}
                </tr>
              ))}
              {data.length === 0 && (
                <tr>
                  <td colSpan="9" className="text-center py-4 text-muted">No users found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default UsersTable;